import { SurveyTemplate } from '@/surveys/state/types';

export const en = {
  dashboard: {
    pageTitle: 'Homepage',
    welcome: {
      title: 'Hello {{name}}!',
      description:
        'Complete the setup to fully leverage the power of AI: once the main actions are completed, the dashboard with the collected data will be available.',
      descriptionHasData: 'Here is an overview of your organization’s status',
    },
    noEmployees: {
      employees: {
        title: 'Add employee records',
        description:
          'Upload employee records and complete profiles with essential info: AI organizes and interprets the data for you.',
        cta: 'Go to Records',
      },
      reviewData: {
        title: 'Fix employee records',
        description:
          'Complete and update employee profiles: only complete profiles can receive the assessment.',
        cta: 'Review and fix',
      },
      surveys: {
        title: 'Send the assessment',
        description:
          'Start measuring soft skills and gather the first insights about your organization.',
        cta: 'Go to Assessment',
      },
    },
    surveysBox: {
      title: 'Start using the platform',
      steps: {
        step1: {
          title: 'Fix employee records',
          description:
            'Complete and update employee profiles: only complete profiles can receive the assessment.',
          ctaLabel: 'Review and fix',
        },
        step2: {
          title: 'Soft skill assessment',
          description:
            'Send the soft skills questionnaire and gather the first insights about your organization. Remember that only complete profiles can receive the assessment.',
          ctaLabel: 'Start assessment',
        },
        step3: {
          title: 'Hard skill assessment',
          description:
            'Send the hard skills questionnaire and gather the first insights about your organization. Remember that only complete profiles can receive the assessment.',
          ctaLabel: 'Start assessment',
        },
        step4: {
          title: 'Add employee records',
          description:
            'Upload employee records and complete profiles with essential info: AI organizes and interprets the data for you.',
          ctaLabel: 'Add records',
        },
      },
      cta: 'Go to Assessment',
    },
    overview: {
      title: 'Company overview',
      current: {
        title: 'Total records',
        tooltip: 'Total records uploaded to the system, including incomplete ones',
      },
      validProfile: {
        title: 'Complete profiles',
        tooltip: 'Records with all essential information completed',
      },
      invalidProfile: {
        title: 'Profiles to fix',
        tooltip: 'Records with missing or incomplete information',
      },
      noInvalid: {
        text1: 'All profiles have been uploaded correctly! ',
        text2: 'Proceed with sending the assessment',
      },
      cta: {
        fixProfiles: 'Review and fix',
        goToPeople: 'Go to Records',
      },
      departments: {
        title: 'Departments',
        cta: 'Go to Departments',
        noDepartmentName: 'No name',
      },
    },
    overviewSurveys: {
      title: 'Assessment overview',
      description: 'Select a questionnaire from the list below to view its average score.',
      results: {
        title: '{{surveyTemplateName}} assessment responses',
        description: {
          singular: '{{total}} questionnaire sent',
          plural: '{{total}} questionnaires sent',
        },
        cta: 'Go to {{surveyTemplateName}} assessment',
        completed: 'Completed',
        notCompleted: 'To complete',
        completionRate: 'Completion rate',
        info: {
          noData:
            'No results yet: the dashboard will be visible only when at least one response is collected',
        },
        completedBox: {
          title: 'Average score for {{surveyTemplateName}}',
          description:
            'The chart shows the average scores (scale 0-7) on the {{metricsCount}} {{surveyTemplateName}}, considered essential to effectively face AI challenges.',
        },
      },
    },
    learning: {
      title: 'Hello {{firstName}}!',
      description: {
        hasNeverCompletedSurveys:
          'You have not completed any assessment yet. Complete your first assessment to view your results and get personalized insights on your skills.',
        hasNotCompletedSurveys:
          'Complete the assessments to gain insights into your skills and development areas',
        hasCompletedSurveys: 'Great job! Explore your next steps',
      },
      surveyNotCompleted: {
        title: 'Complete your assessments',
        steps: {
          title: 'Assessments to complete',
          description: {
            [SurveyTemplate.SoftSkillAssessment]:
              'A 15-minute questionnaire designed to measure key soft skills — such as creativity, problem solving, collaboration, and empathy — of your company and provide a personalized profile. The results allow you to identify strengths, improvement areas, and compare them with company trends.',
            [SurveyTemplate.HardSkillAssessment]:
              'A 15-minute questionnaire designed to measure the level of maturity in generic technical skills related to AI management and provide a personalized profile. The results allow you to identify strengths, improvement areas, and compare them with company trends.',
          },
          ctaLabel: 'Start assessment',
        },
      },
      surveyResults: {
        title: {
          moreThanOne: 'Completed assessments',
          single: 'Actual Profile - {{surveyTemplateName}}',
        },
        description:
          'The chart shows your average score (scale 0-7) on the {{metricsCount}} {{surveyTemplateName}}, considered essential to effectively face AI challenges.',
        insightBox: {
          title: 'Insights',
          proTitle: 'Strengths',
          conTitle: 'Weaknesses',
        },
      },
      noResultsVisible: {
        description:
          'You have completed the assessment dedicated to evaluating the metrics of the {{surveyTemplateName}} assessment.\nExplore the available content and dive deeper into the areas that interest you most in the Training section.',
        buttonLabel: 'Explore training',
      },
      courses: {
        title: 'Courses for you',
        description: 'Discover courses designed to develop your soft skills.',
      },
      teamDepartment: {
        title: 'Team',
        noDepartmentName: 'No name',
        cta: 'Go to department',
        ctaViewTeam: 'View',
      },
      teamList: {
        title: 'Team members',
        cta: 'Go to team',
      },
      tutorial: {
        employee: {
          title: 'Welcome to Slash',
          slides: {
            slide1: {
              title: 'View your assessment results',
              description:
                'From the Homepage you can check your assessment results. This gives you a clear view of your overall readiness level and helps you identify areas to focus on to develop your skills.',
            },
            slide2: {
              title: 'Ask your AI assistant',
              description:
                'Your AI assistant is available to answer your questions about the platform.',
            },
            slide3: {
              title: 'Take a look at your results',
              description:
                'Access the assessment section to view aggregated data and compare your results with your team, identifying strengths and areas for improvement.',
            },
            slide4: {
              title: 'Explore the Training section',
              description:
                'The Training section is your gateway to Slash’s learning paths. Here you’ll find an area dedicated to soft skill development with in-person activities and workshops, and a section designed to boost your hard skills directly on the platform through video pills, PDF content, and interactive quizzes.',
            },
          },
        },
        manager: {
          title: 'Welcome to Slash',
          slides: {
            slide1: {
              title: 'View your assessment results',
              description:
                'From the Homepage you can check your assessment results. This gives you a clear view of your readiness level and helps you identify areas to focus on to develop your skills.',
            },
            slide2: {
              title: 'View your department results',
              description:
                'In the My Department section you can explore aggregated results for your department and its teams: compare scores with the company average, view individual soft skills, and see each collaborator’s positioning.',
            },
            slide3: {
              title: 'Take a look at your soft results',
              description:
                'Access the assessment section to view aggregated data and compare your results with your department and the entire organization, identifying strengths and areas for improvement.',
            },
            slide4: {
              title: 'Explore the Training section',
              description:
                'The Training section is your gateway to Slash’s learning paths. Here you’ll find an area dedicated to soft skill development with in-person activities and workshops, and a section designed to boost your hard skills directly on the platform through video pills, PDF content, and interactive quizzes.',
            },
            slide5: {
              title: 'Ask your AI assistant',
              description:
                'Your AI assistant is available to answer your questions about the platform.',
            },
          },
        },
        team: {
          title: 'Welcome to Slash',
          slides: {
            slide1: {
              title: 'View your assessment results',
              description:
                'From the Homepage you can check your assessment results. This gives you a clear view of your overall readiness level and helps you identify areas to focus on to develop your skills.',
            },
            slide2: {
              title: 'View your team results',
              description:
                'Access the My Team section to view average and individual scores of team members, compare them with department averages, and identify strengths and improvement areas.',
            },
            slide3: {
              title: 'Take a look at your results',
              description:
                'Access the assessment section to view aggregated data and compare your results with your team and department, identifying strengths and areas for improvement.',
            },
            slide4: {
              title: 'Explore the Training section',
              description:
                'The Training section is your gateway to Slash’s learning paths. Here you’ll find an area dedicated to soft skill development with in-person activities and workshops, and a section designed to boost your hard skills directly on the platform through video pills, PDF content, and interactive quizzes.',
            },
            slide5: {
              title: 'Ask your AI assistant',
              description:
                'Your AI assistant is available to answer your questions about the platform.',
            },
          },
        },
      },
    },
  },
};
